import React, { useEffect } from 'react';
import { Form } from '@fiverr-private/forms';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Heading, Typography } from '@fiverr-private/typography';
import { OnboardingFooter, useIdentificationContext } from '@fiverr-private/identification_core';
import { appTranslate } from '../../i18n';
import { FormSubmitButton } from '../../shared/components/FormSubmitButton';
import { globalBigQueryReporter, globalGrafanaReporter, globalMixpanelReporter } from '../../events';
import { useStepsContext } from '../StepsContext';
import { useProfileSetupForm } from './hooks/useProfileSetupForm';
import { UsernameInput } from './UsernameInput';
import { useSubmitHandler } from './hooks/useSubmitHandler';
import { CHOOSE_WISELY_COPY_KEY } from './UsernameInput/UsernameInput.constants';

interface ButtonPayload {
    fullWidth: boolean;
}

export const ProfileSetupStep = () => {
    const { methods, loading, usernameAvailable, onUsernameChange, setLoading } = useProfileSetupForm();
    const { isSocialFlow } = useIdentificationContext();
    const { variant } = useIdentificationContext();
    const { goBack, hasBack } = useStepsContext();
    const isStandaloneOnboarding = variant === 'onboarding';
    const onSubmit = useSubmitHandler({ setLoading, setError: methods.setError, isSocialFlow });

    useEffect(() => {
        globalGrafanaReporter.count('identification.%{platform}.profile_setup.show');
        globalMixpanelReporter.send('Identification - Profile Setup - Show');
        globalBigQueryReporter.send('identification.profile_setup.show');
    }, []);

    const getFormSubmitButton = ({ fullWidth }: ButtonPayload) => (
        <FormSubmitButton loading={loading} fullWidth={fullWidth}>
            {appTranslate('steps.profile_setup.create_account')}
        </FormSubmitButton>
    );

    return (
        <Container display="flex" direction="column" flexGrow="1" minHeight={0}>
            <Form flexGrow="1" methods={methods} onSubmit={onSubmit} minHeight={0}>
                <Stack
                    gap="0"
                    direction="column"
                    minHeight={0}
                    height="100%"
                    overflowY="auto"
                    maxWidth={isStandaloneOnboarding ? '562px' : undefined}
                    padding="px"
                >
                    <Heading as={isStandaloneOnboarding ? 'h3' : 'h4'}>
                        {appTranslate('steps.profile_setup.title')}
                    </Heading>

                    <Typography marginTop="2" size="b_md" color="grey_1000">
                        {appTranslate('steps.profile_setup.subtitle')}
                    </Typography>

                    <Container
                        display={{
                            default: 'none',
                            md: 'block',
                        }}
                        marginTop="2"
                    >
                        <Typography size="b_sm" color="grey_1000" fontWeight="bold">
                            {appTranslate(CHOOSE_WISELY_COPY_KEY)}
                        </Typography>
                    </Container>

                    <Stack direction="column" gap="6" flexGrow="1">
                        <Container
                            marginTop={{
                                default: '7',
                                md: '8',
                            }}
                        >
                            <UsernameInput success={usernameAvailable} loading={loading} onChange={onUsernameChange} />
                        </Container>

                        {!isStandaloneOnboarding && (
                            <Container
                                marginTop={{
                                    md: '0',
                                    default: 'auto',
                                }}
                            >
                                {getFormSubmitButton({ fullWidth: true })}
                            </Container>
                        )}
                    </Stack>
                </Stack>

                {isStandaloneOnboarding && (
                    <Container marginTop="auto">
                        <OnboardingFooter goBack={hasBack ? goBack : undefined}>
                            {getFormSubmitButton({ fullWidth: false })}
                        </OnboardingFooter>
                    </Container>
                )}
            </Form>
        </Container>
    );
};
